import { Charge } from '@willow/types-iso';

/**
 * Filters charges that don't have a CHARGED status
 * By default deferred charges are not included
 *
 * The liquidation payment's fee/advance breakdowns settle what the proceeds covered; a charge
 * the proceeds only partially covered stays CHARGED with its residual amountRemaining, but is
 * excluded here so the residual doesn't flow into borrower amount-due totals or get picked up
 * for payment distribution.
 *
 * Pre-petition (Chapter 13) charges are treated like deferred charges: excluded from borrower
 * billing but INCLUDED when `includeDeferred` is set, so payoff still collects them in full.
 */
export function getChargesDue<
  // `isPrePetition` is widened to allow null so GraphQL-generated charge types (nullable Boolean)
  // satisfy the constraint alongside the domain Charge (boolean | undefined); both are falsy-checked below.
  T extends Pick<Charge, 'status' | 'isDeferred' | 'dIsFulfilledByLiquidation'> & { isPrePetition?: boolean | null },
>(charges: T[], includeDeferred?: boolean): T[] {
  return charges.filter((charge) => {
    if (charge.dIsFulfilledByLiquidation) {
      return false;
    }
    const isChargedStatus = charge.status === 'CHARGED';
    return includeDeferred ? isChargedStatus : isChargedStatus && !charge.isDeferred && !charge.isPrePetition;
  });
}

/*
 * getLateFeesFirstThenOtherChargesDue
 *
 * Return oldest to newest late fees first, then oldest to newest (advances and non late fees) sorted by charge date
 */
export function getLateFeesFirstThenOtherChargesDue(charges: Charge[], includeDeferred?: boolean): Charge[] {
  const nonDeferredCharges = getChargesDue(charges, includeDeferred);

  const sorted = nonDeferredCharges.slice().sort((a, b) => a.chargeDate.getTime() - b.chargeDate.getTime());
  return [
    ...sorted.filter((charge) => charge.type === 'fee' && charge.lineItems.some((item) => item.type === 'LATE')),
    ...sorted.filter(
      (charge) =>
        charge.type === 'advance' || (charge.type === 'fee' && charge.lineItems.every((item) => item.type !== 'LATE')),
    ),
  ];
}
